import React from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Input, Button, Chip, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import useProductSearch from "../hooks/use-product-search";

const SEARCH_LIMIT = 20;

const useQueryParams = () => {
  const location = useLocation();
  return React.useMemo(() => new URLSearchParams(location.search), [location.search]);
};

const SearchPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useQueryParams();
  const query = params.get("q") || "";
  const categoryFilter = params.get("category") || "";
  const aromaFilter = params.get("aroma") || "";
  const offsetParam = Number(params.get("offset") || 0);
  const offset = Number.isNaN(offsetParam) ? 0 : Math.max(offsetParam, 0);

  const [inputValue, setInputValue] = React.useState(query);
  React.useEffect(() => {
    setInputValue(query);
  }, [query]);

  const { results, count, isLoading, error, facets, next, previous } = useProductSearch({
    query,
    limit: SEARCH_LIMIT,
    offset,
    includeFacets: true,
    category: categoryFilter || null,
    aroma: aromaFilter || null,
    enabled: !!query
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    const search = new URLSearchParams();
    if (trimmed) search.set("q", trimmed);
    if (categoryFilter) search.set("category", categoryFilter);
    if (aromaFilter) search.set("aroma", aromaFilter);
    history.push(trimmed ? `/search?${search.toString()}` : "/search");
  };

  const updateFilter = (key: "category" | "aroma", value: string) => {
    const search = new URLSearchParams(location.search);
    if (value) search.set(key, value);
    else search.delete(key);
    search.delete("offset");
    history.push(`/search?${search.toString()}`);
  };

  const changePage = (direction: "next" | "prev") => {
    const search = new URLSearchParams(location.search);
    const delta = direction === "next" ? SEARCH_LIMIT : -SEARCH_LIMIT;
    const nextOffset = Math.max((Number(search.get("offset")) || 0) + delta, 0);
    if (nextOffset === 0) search.delete("offset");
    else search.set("offset", String(nextOffset));
    history.push(`/search?${search.toString()}`);
  };

  const hasNext = Boolean(next) || (typeof count === "number" && offset + SEARCH_LIMIT < count);
  const hasPrev = Boolean(previous) || offset > 0;

  const renderMatches = (match?: { name?: boolean; article?: boolean; aroma?: string[] }) => {
    if (!match) return null;
    const tags = [] as React.ReactNode[];
    if (match.name) tags.push(<Chip key="name" size="sm" variant="flat" color="primary" className="rounded-none">Название</Chip>);
    if (match.article) tags.push(<Chip key="article" size="sm" variant="flat" color="primary" className="rounded-none">Артикул</Chip>);
    if (match.aroma && match.aroma.length) {
      tags.push(
        <Chip key="aroma" size="sm" variant="flat" color="primary" className="rounded-none">
          Аромат: {match.aroma.slice(0, 2).join(", ")}{match.aroma.length > 2 ? "…" : ""}
        </Chip>
      );
    }
    if (!tags.length) return null;
    return <div className="flex flex-wrap gap-2">{tags}</div>;
  };

  return (
    <div className="py-8 md:py-12">
      <Helmet>
        <title>Поиск товаров — Интернет магазин SAFONOVA</title>
        <meta name="description" content="Поиск по каталогу SAFONOVA: ароматы, артикулы, названия." />
      </Helmet>
      <div className="container mx-auto px-6 space-y-8">
        <div>
          <p className="text-xs uppercase tracking-[0.4em] text-foreground/50">Поиск</p>
          <h1 className="text-3xl md:text-4xl font-light">Найдите нужный аромат</h1>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-3">
          <Input
            variant="bordered"
            size="lg"
            placeholder="Например, Лаванда, CND-001, Диффузор"
            value={inputValue}
            onValueChange={setInputValue}
            classNames={{ inputWrapper: 'rounded-none' }}
            endContent={inputValue && (
              <button type="button" className="text-foreground/60" onClick={() => setInputValue("")}>
                <Icon icon="lucide:x" width={18} />
              </button>
            )}
          />
          <Button type="submit" color="primary" size="lg" className="rounded-none">
            Искать
          </Button>
        </form>

        {query ? (
          <p className="text-sm text-foreground/70">
            Найдено {count ?? 0} результатов по запросу «{query}»
          </p>
        ) : (
          <p className="text-sm text-foreground/70">Введите запрос, чтобы увидеть результаты.</p>
        )}

        {query && (
          <div className="space-y-4">
            {facets?.categories && facets.categories.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-foreground/50 mb-2">Категории</p>
                <div className="flex flex-wrap gap-2">
                  {facets.categories.map((cat) => (
                    <Button
                      key={cat.key}
                      size="sm"
                      variant={categoryFilter === cat.key ? "solid" : "flat"}
                      color="primary"
                      className="rounded-none"
                      onPress={() => updateFilter("category", categoryFilter === cat.key ? "" : cat.key)}
                    >
                      {cat.name} ({cat.count})
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {facets?.aromas && facets.aromas.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-foreground/50 mb-2">Ароматы</p>
                <div className="flex flex-wrap gap-2">
                  {facets.aromas.map((aroma) => (
                    <Button
                      key={aroma.id}
                      size="sm"
                      variant={String(aromaFilter) === String(aroma.id) ? "solid" : "flat"}
                      color="secondary"
                      className="rounded-none"
                      onPress={() => updateFilter("aroma", String(aroma.id) === aromaFilter ? "" : String(aroma.id))}
                    >
                      {aroma.name} ({aroma.count})
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="min-h-[200px]">
          {!query && (
            <div className="text-center py-12 text-foreground/60">Начните вводить запрос, чтобы отобразить результаты.</div>
          )}

          {query && isLoading && (
            <div className="flex items-center gap-3 text-foreground/70">
              <Spinner /> Загрузка результатов...
            </div>
          )}

          {query && error && (
            <div className="text-danger">{error.message}</div>
          )}

          {query && !isLoading && !error && !results.length && (
            <div className="text-foreground/70">Ничего не найдено. Попробуйте изменить запрос.</div>
          )}

          {results.length > 0 && (
            <div className="space-y-4">
              {results.map((item) => (
                <Link
                  key={item.id}
                  to={`/product-detail/${item.slug || item.id}`}
                  className="flex flex-col md:flex-row gap-4 border border-divider p-4 hover:border-primary/50 transition"
                >
                  <div className="w-full md:w-40 h-32 bg-content2/70 flex items-center justify-center overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs uppercase tracking-wide text-foreground/50">Нет фото</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-lg font-medium text-foreground">{item.name}</h3>
                      {item.article && <span className="text-xs text-foreground/60">Арт. {item.article}</span>}
                    </div>
                    <div className="text-sm text-foreground/70 flex items-center gap-2">
                      {item.category?.name && <span>{item.category.name}</span>}
                    </div>
                    {renderMatches(item.matches)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {query && (hasPrev || hasNext) && (
          <div className="flex items-center justify-between gap-4 pt-4">
            <Button variant="flat" isDisabled={!hasPrev} onPress={() => changePage("prev")} className="rounded-none">
              <Icon icon="lucide:arrow-left" width={16} /> Назад
            </Button>
            <p className="text-sm text-foreground/60">
              Страница {Math.floor(offset / SEARCH_LIMIT) + 1} из {count ? Math.max(1, Math.ceil(count / SEARCH_LIMIT)) : '…'}
            </p>
            <Button variant="flat" isDisabled={!hasNext} onPress={() => changePage("next")} className="rounded-none">
              Вперёд <Icon icon="lucide:arrow-right" width={16} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
